import { getRevenuesCommunication } from 'api/v2/revenues';
import {
  GeneratedInvoiceFormValues,
  GeneratedInvoiceSubmitValues,
  InstallmentAmountTypeEnum,
} from 'types/invoice.types';
import { divideBy1000, multiplyBy1000, round } from 'utils/math';
import { getInvoiceItemsTotalInclVAT } from '../../OtherRevenueForm/utils';
import '../styles.scss';
import formatMoney from 'utils/formatMoney';
import appI18nInstance from 'i18n';

export const getCommunicationDependencies = (
  values: GeneratedInvoiceFormValues,
) => {
  return {
    clientId: values.clientId,
    docNumber: values.revenueNumber,
    useCommunication: !!values.settings.useCommunication,
  };
};

export const getQRCodeDependencies = (values: GeneratedInvoiceFormValues) => {
  const copyrightCalcs = values.itemsCalculations?.copyRight;
  const copyrightFees = copyrightCalcs?.copyrightRevenueTaxAmount || 0;
  return {
    IBAN: values.user.IBAN,
    currency: values.currency!,
    amount: round(
      Math.max(
        getInvoiceItemsTotalInclVAT(values.items || []) -
          divideBy1000(values.downPayment || 0) -
          (values.settings.includeCopyrightReminder ? copyrightFees : 0),
        0,
      ),
    ),
    name:
      values.user.accountHolderName ||
      values.user.companyName ||
      `${values.user.firstName} ${values.user.lastName}`,
    communication: values.settings.useCommunication
      ? values.communication
      : values.revenueNumber,
    usePaymentQrCode: !!values.settings.usePaymentQrCode,
  };
};

export const communicationGuard = async (
  values: GeneratedInvoiceSubmitValues,
) => {
  let communication = values.communication || undefined;

  if (
    values.settings.useCommunication &&
    values.clientId &&
    values.revenueNumber &&
    !communication
  ) {
    communication = await getRevenuesCommunication(
      values.clientId,
      values.revenueNumber,
    );
  }

  return communication;
};

export const getProjectRemainingAmountAfterInvoice = (
  values: GeneratedInvoiceFormValues,
) => {
  const projectAmount = getInvoiceItemsTotalInclVAT(
    values.revenueProject?.snapshot?.items || [],
  );

  if (!projectAmount) return 0;

  const remainingAmount = getProjectRemainingAmount(values).fixed;

  const isFixed =
    values.revenueProject?.installmentAmountShownAs ===
    InstallmentAmountTypeEnum.fixed;

  const installmentValue = values.revenueProject?.installmentValue || 0;

  const installmentPercentage =
    values.revenueProject?.installmentPercentage || 0;

  const newInvoicePercentage = isFixed
    ? divideBy1000(installmentValue) / projectAmount
    : installmentPercentage / 1000;

  const newInvoiceAmount = Math.min(
    projectAmount * newInvoicePercentage,
    remainingAmount,
  );

  return Math.max(round(remainingAmount - newInvoiceAmount), 0);
};

export const getProjectRemainingAmount = (
  values: GeneratedInvoiceFormValues,
) => {
  const projectAmount = getInvoiceItemsTotalInclVAT(
    values.revenueProject?.snapshot?.items || [],
  );

  if (!projectAmount) return { fixed: 0, percentage: 0 };

  const invoicesAmount = divideBy1000(
    values.revenueProject?.snapshot?.revenues?.reduce(
      (acc, curr) => acc + curr.totalAmountInclVAT,
      0,
    ) || 0,
  );

  const fixed = projectAmount - invoicesAmount;

  const percentage = fixed / projectAmount;

  return {
    fixed: round(fixed),
    percentage: round(percentage, 10000),
  };
};

type InvoiceErrorsContext = {
  values: GeneratedInvoiceFormValues;
  language?: string;
};
export const getInvoiceErrorsContext = (args: InvoiceErrorsContext) => {
  const { values, language } = args;
  const projectRemaining = getProjectRemainingAmount(values);
  return {
    projectRemainingFixed: formatMoney(
      projectRemaining.fixed || 0,
      values.currency,
      language || appI18nInstance.language,
    ),
    projectRemainingPercentage: `${round(
      (projectRemaining.percentage || 0) * 100,
    )}%`,
  };
};
